import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ensureTalkingPhoto,
  generateStoryboard,
  generateVideoAgent,
  generateVideoHeygen,
  getCharacterPhotos,
  getHeygenVoices,
  getMediaJobs,
  getRoleVideoPacks,
  getVideoAssets,
  getVisibleCharacters,
  requestRoleVideoStrategy,
  setHeygenVoice,
  type DbAsset,
  type DbCharacter,
  type DbHeygenVoice,
  type MemberMediaJob,
  type RoleVideoPack,
  type Storyboard,
} from '@/api/astraea';
import HeygenVoiceSelect from '@/components/HeygenVoiceSelect';

type Mode = 'single' | 'storyboard' | 'translation';

interface RoleVideoTemplate {
  key: string;
  label: string;
  icon: string;
  platform: string;
  contentGoal: string;
  script: string;
  scene: string;
}

const TEMPLATES: RoleVideoTemplate[] = [
  {
    key: 'ad',
    label: '软性广告',
    icon: 'ri-megaphone-line',
    platform: 'douyin',
    contentGoal: 'soft_offer',
    script: '我最近把这个方法重新整理了一遍。不是让你马上决定,先看这三个判断点,你会少走很多弯路。',
    scene: '竖屏生活感口播,角色坐在明亮书桌前,语气自然,画面干净,结尾引导留言领取检查清单',
  },
  {
    key: 'education',
    label: '教育课程',
    icon: 'ri-graduation-cap-line',
    platform: 'xiaohongshu',
    contentGoal: 'teach_framework',
    script: '很多人不是学不会,是第一步就选错了。先别急着买课,我用一个小例子讲清楚。',
    scene: '竖屏轻教学,角色在白板旁用便签解释,镜头半身,节奏清楚但不要像公开课',
  },
  {
    key: 'expert',
    label: '专家解说',
    icon: 'ri-user-star-line',
    platform: 'wechat_channels',
    contentGoal: 'expert_explainer',
    script: '这个问题表面看是选择题,实际是风险顺序。先判断成本,再判断时间,最后才看工具。',
    scene: '竖屏专业解说,角色在简洁办公环境,自然抬手说明,字幕突出三个判断点',
  },
  {
    key: 'howto',
    label: '操作教学',
    icon: 'ri-tools-line',
    platform: 'kuaishou',
    contentGoal: 'how_to',
    script: '你照这个顺序试一次。第一步先把素材分三类,第二步只改开头,第三步再看数据。',
    scene: '竖屏步骤演示,角色在电脑桌前,穿插屏幕动作感,语气像朋友教朋友',
  },
  {
    key: 'news',
    label: '新闻简报',
    icon: 'ri-newspaper-line',
    platform: 'douyin',
    contentGoal: 'news_brief',
    script: '今天这条变化值得看一下。它影响的不是所有人,但如果你正在做这件事,要先改一个动作。',
    scene: '竖屏快讯简报,角色站在干净背景前,节奏克制,重点信息卡片从侧边进入',
  },
  {
    key: 'podcast',
    label: '视频播客',
    icon: 'ri-mic-2-line',
    platform: 'bilibili',
    contentGoal: 'video_podcast',
    script: '这期我想聊一个挺真实的问题:为什么同样的内容,有的人越做越稳,有的人越做越乱。',
    scene: '竖屏播客切片,角色坐在桌前面对麦克风,灯光柔和,像一段真实访谈摘录',
  },
];

const MODE_TABS: { key: Mode; label: string; icon: string }[] = [
  { key: 'single', label: '单场景', icon: 'ri-video-on-line' },
  { key: 'storyboard', label: '分镜', icon: 'ri-layout-masonry-line' },
  { key: 'translation', label: '翻译', icon: 'ri-translate-2' },
];

const STATUS_TONE: Record<string, string> = {
  completed: 'bg-green-50 text-green-700 border-green-100',
  processing: 'bg-primary-50 text-primary-700 border-primary-100',
  pending: 'bg-amber-50 text-amber-700 border-amber-100',
  failed: 'bg-red-50 text-red-600 border-red-100',
  timeout: 'bg-red-50 text-red-600 border-red-100',
};

function toList(value: unknown): string[] {
  return Array.isArray(value) ? value.map((v) => String(v)).filter(Boolean) : [];
}

function textFrom(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function PackText({ pack }: { pack: RoleVideoPack }) {
  const script = pack.script || {};
  const cover = pack.cover || {};
  const privateDomain = pack.private_domain || {};
  const main = textFrom(script.main_script);
  const hook = textFrom(script.opening_hook);
  const ending = textFrom(script.ending_hook);
  const title = textFrom(cover.title) || textFrom(script.title) || pack.content_type || '角色视频草稿';
  return (
    <div className="min-w-0">
      <div className="text-sm font-semibold text-foreground-950 truncate">{title}</div>
      <div className="mt-1 text-[11px] text-secondary-500 truncate">
        {pack.platform || '未指定平台'} · {pack.status || 'brief'} · {new Date(pack.created_at).toLocaleString()}
      </div>
      {(hook || main || ending) && (
        <p className="mt-2 text-xs text-foreground-600 line-clamp-3">
          {[hook, main, ending].filter(Boolean).join(' ')}
        </p>
      )}
      {textFrom(privateDomain.public_reply) && (
        <p className="mt-2 text-[11px] text-primary-700 line-clamp-2">
          承接回复: {textFrom(privateDomain.public_reply)}
        </p>
      )}
    </div>
  );
}

function EmptyState({ icon, title, text }: { icon: string; title: string; text: string }) {
  return (
    <div className="py-10 text-center text-sm text-secondary-400">
      <i className={`${icon} text-3xl text-secondary-300`} />
      <div className="mt-2 font-medium text-foreground-700">{title}</div>
      <div className="mt-1 text-xs">{text}</div>
    </div>
  );
}

export default function RoleVideoPage() {
  const [characters, setCharacters] = useState<DbCharacter[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [voices, setVoices] = useState<DbHeygenVoice[]>([]);
  const [packs, setPacks] = useState<RoleVideoPack[]>([]);
  const [jobs, setJobs] = useState<MemberMediaJob[]>([]);
  const [videos, setVideos] = useState<DbAsset[]>([]);
  const [photos, setPhotos] = useState<DbAsset[]>([]);
  const [mode, setMode] = useState<Mode>('single');
  const [templateKey, setTemplateKey] = useState(TEMPLATES[0].key);
  const [platform, setPlatform] = useState(TEMPLATES[0].platform);
  const [goal, setGoal] = useState(TEMPLATES[0].contentGoal);
  const [scene, setScene] = useState(TEMPLATES[0].scene);
  const [script, setScript] = useState(TEMPLATES[0].script);
  const [voiceId, setVoiceId] = useState('');
  const [maxSeconds, setMaxSeconds] = useState(30);
  const [sourceLanguage, setSourceLanguage] = useState('中文');
  const [targetLanguage, setTargetLanguage] = useState('英文');
  const [storyboard, setStoryboard] = useState<Storyboard | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<{ tone: 'ok' | 'error' | 'info'; text: string } | null>(null);

  const selected = useMemo(
    () => characters.find((c) => c.id === selectedId) || characters[0] || null,
    [characters, selectedId],
  );
  const selectedTemplate = TEMPLATES.find((t) => t.key === templateKey) || TEMPLATES[0];
  const variants = selected?.visual_variants || {};
  const rolePacks = packs.filter((p) => p.character_id === selected?.id);
  const processingJobs = jobs.filter((j) => ['pending', 'processing'].includes(j.display_status));

  const load = useCallback(async () => {
    const cs = await getVisibleCharacters();
    setCharacters(cs);
    setSelectedId((prev) => prev ?? cs[0]?.id ?? null);
    const ids = cs.map((c) => c.id);
    const [pv, j] = await Promise.all([
      getRoleVideoPacks(ids),
      getMediaJobs(40),
    ]);
    setPacks(pv);
    setJobs(j);
  }, []);

  const loadCharacterAssets = useCallback(async (characterId: number) => {
    const [v, p] = await Promise.all([
      getVideoAssets(characterId),
      getCharacterPhotos(characterId),
    ]);
    setVideos(v);
    setPhotos(p);
  }, []);

  useEffect(() => {
    load().catch((e) => setNote({ tone: 'error', text: e instanceof Error ? e.message : '读取失败' }));
    getHeygenVoices().then(setVoices).catch(() => setVoices([]));
  }, [load]);

  useEffect(() => {
    if (!selected) return;
    setVoiceId(selected.heygen_voice_id || '');
    loadCharacterAssets(selected.id).catch(() => {});
  }, [selected, loadCharacterAssets]);

  useEffect(() => {
    if (processingJobs.length === 0) return;
    const t = window.setInterval(() => load().catch(() => {}), 15000);
    return () => window.clearInterval(t);
  }, [load, processingJobs.length]);

  const applyTemplate = (key: string) => {
    const t = TEMPLATES.find((item) => item.key === key) || TEMPLATES[0];
    setTemplateKey(t.key);
    setPlatform(t.platform);
    setGoal(t.contentGoal);
    setScene(t.scene);
    setScript(t.script);
    setStoryboard(null);
    setNote(null);
  };

  const runWithCharacter = async (label: string, fn: (character: DbCharacter) => Promise<void>) => {
    if (!selected) {
      setNote({ tone: 'error', text: '请先新建或选择一个角色' });
      return;
    }
    setBusy(label);
    setNote({ tone: 'info', text: '正在送出请求…' });
    try {
      await fn(selected);
      await load();
      await loadCharacterAssets(selected.id);
    } catch (e) {
      setNote({ tone: 'error', text: e instanceof Error ? e.message : '操作失败' });
    } finally {
      setBusy(null);
    }
  };

  const saveVoice = () => runWithCharacter('voice', async (character) => {
    if (!voiceId) throw new Error('请先选择一个视频声音');
    await setHeygenVoice(character.id, voiceId);
    setNote({ tone: 'ok', text: '视频声音已绑定' });
  });

  const prepareTalkingPhoto = () => runWithCharacter('photo', async (character) => {
    const r = await ensureTalkingPhoto(character.id);
    if (r.ok === false) throw new Error(r.message || '形象初始化失败');
    setNote({ tone: 'ok', text: '角色形象已进入视频引擎缓存' });
  });

  const createStrategyPack = () => runWithCharacter('strategy', async (character) => {
    const r = await requestRoleVideoStrategy(character.id, platform, goal);
    if (r.ok === false) throw new Error(r.message || '策略草稿生成失败');
    setNote({ tone: 'ok', text: '项目草稿已生成' });
  });

  const generateSingle = () => runWithCharacter('single', async (character) => {
    const merged = `${script.trim()}\n\n画面要求:${scene.trim()}`;
    const r = await generateVideoHeygen(character.id, null, merged, platform);
    if (r.ok === false) throw new Error(r.message || '视频提交失败');
    setNote({ tone: 'ok', text: '单场景视频已进入生成队列' });
  });

  const createStoryboard = () => runWithCharacter('storyboard-draft', async (character) => {
    const r = await generateStoryboard(character.id, `${selectedTemplate.label}:${scene}\n脚本方向:${script}`, maxSeconds);
    if (r.ok === false) throw new Error('分镜草稿生成失败');
    setStoryboard(r);
    setNote({ tone: 'ok', text: '分镜草稿已生成,确认后再渲染' });
  });

  const renderStoryboard = () => runWithCharacter('storyboard-render', async (character) => {
    const prompt = storyboard?.video_prompt || `${scene}\n口播:${script}`;
    const r = await generateVideoAgent(character.id, null, prompt, maxSeconds);
    if (r.ok === false) throw new Error(r.message || '分镜渲染提交失败');
    setNote({ tone: 'ok', text: '分镜视频已进入生成队列' });
  });

  const renderTranslation = () => runWithCharacter('translation', async (character) => {
    const prompt = [
      `Create a vertical role video for ${character.name}.`,
      `Translate and adapt the narration from ${sourceLanguage} to ${targetLanguage}.`,
      `Keep the meaning natural, keep the role identity consistent, and preserve timing under ${maxSeconds} seconds.`,
      `Source narration: ${script.trim()}`,
      `Scene: ${scene.trim()}`,
    ].join('\n');
    const r = await generateVideoAgent(character.id, null, prompt, maxSeconds);
    if (r.ok === false) throw new Error(r.message || '翻译视频提交失败');
    setNote({ tone: 'ok', text: '翻译视频已进入生成队列' });
  });

  const actionDisabled = !!busy || !selected;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2 text-xs text-secondary-400 mb-1">
            <Link to="/" className="hover:text-foreground-600">工作台</Link>
            <i className="ri-arrow-right-s-line" />
            <span>角色视频</span>
          </div>
          <h1 className="text-2xl font-bold text-foreground-950">角色视频工作区</h1>
        </div>
        <button
          type="button"
          onClick={() => load().catch(() => {})}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-secondary-200 text-xs font-medium text-foreground-600 bg-background-50 cursor-pointer"
        >
          <i className="ri-refresh-line" />
          刷新
        </button>
      </div>

      <div className="grid lg:grid-cols-[17rem_minmax(0,1fr)_20rem] gap-4 items-start">
        <aside className="space-y-4">
          <section className="rounded-xl border border-secondary-200 bg-background-50 p-3">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-foreground-950">角色库</h2>
              <Link to="/create" className="text-xs text-primary-700 font-medium">新建</Link>
            </div>
            {characters.length === 0 ? (
              <EmptyState icon="ri-user-add-line" title="暂无角色" text="先创建角色后再生成视频。" />
            ) : (
              <div className="space-y-2">
                {characters.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => { setSelectedId(c.id); setStoryboard(null); setNote(null); }}
                    className={`w-full flex items-center gap-2.5 rounded-lg border p-2 text-left cursor-pointer ${
                      selected?.id === c.id ? 'border-primary-300 bg-primary-50' : 'border-secondary-200 bg-background-50 hover:bg-secondary-50'
                    }`}
                  >
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-secondary-100 shrink-0">
                      {c.avatar_url ? <img src={c.avatar_url} alt={c.name} className="w-full h-full object-cover" /> : <i className="ri-user-smile-line h-full flex items-center justify-center text-primary-500" />}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-foreground-900 truncate">{c.name}</div>
                      <div className="text-[11px] text-secondary-500 truncate">{c.description || '未填写定位'}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </section>

          <section className="rounded-xl border border-secondary-200 bg-background-50 p-3">
            <h2 className="text-sm font-semibold text-foreground-950 mb-2">外观变体</h2>
            {selected ? (
              <div className="space-y-3">
                {[
                  ['服装', toList(variants.allowed_outfits)],
                  ['背景', toList(variants.allowed_backgrounds)],
                  ['镜头', toList(variants.allowed_camera_styles)],
                  ['表情', toList(variants.allowed_expressions)],
                ].map(([label, items]) => (
                  <div key={String(label)}>
                    <div className="text-[11px] font-medium text-secondary-500 mb-1">{String(label)}</div>
                    <div className="flex flex-wrap gap-1">
                      {(items as string[]).slice(0, 5).map((item) => (
                        <span key={item} className="px-2 py-1 rounded-md bg-secondary-100 text-[11px] text-foreground-600">{item}</span>
                      ))}
                      {(items as string[]).length === 0 && <span className="text-[11px] text-secondary-400">未配置</span>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState icon="ri-shirt-line" title="未选择角色" text="选择角色后显示外观库。" />
            )}
          </section>
        </aside>

        <main className="space-y-4 min-w-0">
          <section className="rounded-xl border border-secondary-200 bg-background-50 p-4">
            <div className="flex flex-wrap gap-2 mb-4">
              {MODE_TABS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => { setMode(item.key); setNote(null); }}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border cursor-pointer ${
                    mode === item.key ? 'bg-primary-500 text-background-50 border-primary-500' : 'border-secondary-200 text-foreground-600 bg-background-50'
                  }`}
                >
                  <i className={item.icon} />
                  {item.label}
                </button>
              ))}
            </div>

            <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
              {TEMPLATES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => applyTemplate(t.key)}
                  className={`min-h-20 rounded-lg border p-2 text-left cursor-pointer ${
                    templateKey === t.key ? 'border-primary-300 bg-primary-50' : 'border-secondary-200 bg-background-50 hover:bg-secondary-50'
                  }`}
                >
                  <i className={`${t.icon} text-lg text-primary-600`} />
                  <div className="mt-1 text-xs font-semibold text-foreground-800">{t.label}</div>
                </button>
              ))}
            </div>

            <div className="grid sm:grid-cols-3 gap-3 mb-3">
              <label className="block">
                <span className="text-xs font-medium text-foreground-700">平台</span>
                <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="mt-1 w-full rounded-lg border border-secondary-200 bg-background-100 px-3 py-2 text-sm">
                  <option value="douyin">抖音</option>
                  <option value="xiaohongshu">小红书</option>
                  <option value="kuaishou">快手</option>
                  <option value="wechat_channels">视频号</option>
                  <option value="bilibili">B站</option>
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-foreground-700">目标</span>
                <input value={goal} onChange={(e) => setGoal(e.target.value)} className="mt-1 w-full rounded-lg border border-secondary-200 bg-background-100 px-3 py-2 text-sm" />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-foreground-700">秒数</span>
                <input type="number" min={8} max={120} value={maxSeconds} onChange={(e) => setMaxSeconds(Number(e.target.value) || 30)} className="mt-1 w-full rounded-lg border border-secondary-200 bg-background-100 px-3 py-2 text-sm" />
              </label>
            </div>

            {mode === 'translation' && (
              <div className="grid sm:grid-cols-2 gap-3 mb-3">
                <label className="block">
                  <span className="text-xs font-medium text-foreground-700">原语言</span>
                  <input value={sourceLanguage} onChange={(e) => setSourceLanguage(e.target.value)} className="mt-1 w-full rounded-lg border border-secondary-200 bg-background-100 px-3 py-2 text-sm" />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-foreground-700">目标语言</span>
                  <input value={targetLanguage} onChange={(e) => setTargetLanguage(e.target.value)} className="mt-1 w-full rounded-lg border border-secondary-200 bg-background-100 px-3 py-2 text-sm" />
                </label>
              </div>
            )}

            <label className="block mb-3">
              <span className="text-xs font-medium text-foreground-700">{mode === 'translation' ? '原始脚本' : '口播脚本'}</span>
              <textarea value={script} onChange={(e) => setScript(e.target.value)} rows={5} className="mt-1 w-full rounded-lg border border-secondary-200 bg-background-100 px-3 py-2.5 text-sm resize-none focus:outline-none focus:border-primary-400" />
            </label>

            <label className="block">
              <span className="text-xs font-medium text-foreground-700">画面设定</span>
              <textarea value={scene} onChange={(e) => setScene(e.target.value)} rows={3} className="mt-1 w-full rounded-lg border border-secondary-200 bg-background-100 px-3 py-2.5 text-sm resize-none focus:outline-none focus:border-primary-400" />
            </label>

            {note && (
              <div className={`mt-3 rounded-lg px-3 py-2 text-xs ${
                note.tone === 'error' ? 'bg-red-50 text-red-600' : note.tone === 'ok' ? 'bg-green-50 text-green-700' : 'bg-primary-50 text-primary-700'
              }`}>
                {note.text}
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              <button type="button" onClick={createStrategyPack} disabled={actionDisabled} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-primary-200 text-primary-700 bg-primary-50 text-sm font-semibold cursor-pointer disabled:opacity-50">
                <i className="ri-file-list-3-line" />
                {busy === 'strategy' ? '生成中…' : '生成项目草稿'}
              </button>
              {mode === 'single' && (
                <button type="button" onClick={generateSingle} disabled={actionDisabled || !script.trim()} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer disabled:opacity-50">
                  <i className="ri-play-circle-line" />
                  {busy === 'single' ? '提交中…' : '生成单场景视频'}
                </button>
              )}
              {mode === 'storyboard' && (
                <>
                  <button type="button" onClick={createStoryboard} disabled={actionDisabled} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer disabled:opacity-50">
                    <i className="ri-layout-masonry-line" />
                    {busy === 'storyboard-draft' ? '生成中…' : '生成分镜草稿'}
                  </button>
                  <button type="button" onClick={renderStoryboard} disabled={actionDisabled || !storyboard} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-secondary-200 text-foreground-700 bg-background-50 text-sm font-semibold cursor-pointer disabled:opacity-50">
                    <i className="ri-send-plane-line" />
                    {busy === 'storyboard-render' ? '提交中…' : '确认渲染'}
                  </button>
                </>
              )}
              {mode === 'translation' && (
                <button type="button" onClick={renderTranslation} disabled={actionDisabled || !script.trim()} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer disabled:opacity-50">
                  <i className="ri-translate-2" />
                  {busy === 'translation' ? '提交中…' : '生成翻译视频'}
                </button>
              )}
            </div>
          </section>

          {storyboard && (
            <section className="rounded-xl border border-secondary-200 bg-background-50 p-4">
              <div className="flex items-center justify-between gap-2 mb-3">
                <h2 className="text-sm font-semibold text-foreground-950">{storyboard.title || '分镜草稿'}</h2>
                <span className="text-[11px] text-secondary-500">{storyboard.est_seconds || maxSeconds}s / {storyboard.max_seconds || maxSeconds}s</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                {(storyboard.shots || []).map((shot, index) => (
                  <div key={`${shot.scene}-${index}`} className="rounded-lg border border-secondary-200 bg-background-100 p-3">
                    <div className="text-[11px] font-semibold text-primary-700 mb-1">镜头 {index + 1}</div>
                    <p className="text-xs text-foreground-700 leading-relaxed">{shot.scene}</p>
                    <p className="mt-2 text-xs text-secondary-600 leading-relaxed">{shot.narration}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="rounded-xl border border-secondary-200 bg-background-50 p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-foreground-950">项目草稿</h2>
              <span className="text-[11px] text-secondary-400">{rolePacks.length} 个</span>
            </div>
            {rolePacks.length === 0 ? (
              <EmptyState icon="ri-file-list-line" title="暂无项目" text="先生成项目草稿或分镜。" />
            ) : (
              <div className="space-y-2">
                {rolePacks.slice(0, 5).map((pack) => (
                  <div key={pack.id} className="rounded-lg border border-secondary-200 bg-background-100 p-3">
                    <PackText pack={pack} />
                  </div>
                ))}
              </div>
            )}
          </section>
        </main>

        <aside className="space-y-4">
          <section className="rounded-xl border border-secondary-200 bg-background-50 p-3">
            <h2 className="text-sm font-semibold text-foreground-950 mb-2">声音设置</h2>
            <HeygenVoiceSelect
              voices={voices}
              value={voiceId}
              onChange={setVoiceId}
              allowAuto
              autoLabel="自动声音"
              className="w-full rounded-lg border border-secondary-200 bg-background-100 px-3 py-2 text-sm"
            />
            <div className="mt-2 flex gap-2">
              <button type="button" onClick={saveVoice} disabled={actionDisabled || !voiceId} className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold bg-primary-500 text-background-50 cursor-pointer disabled:opacity-50">
                <i className="ri-save-3-line" />
                绑定
              </button>
              <button type="button" onClick={prepareTalkingPhoto} disabled={actionDisabled || !selected?.avatar_url} className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold border border-secondary-200 text-foreground-700 cursor-pointer disabled:opacity-50">
                <i className="ri-user-voice-line" />
                初始化
              </button>
            </div>
          </section>

          <section className="rounded-xl border border-secondary-200 bg-background-50 p-3">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-foreground-950">生成队列</h2>
              <span className="text-[11px] text-secondary-400">{processingJobs.length} 处理中</span>
            </div>
            {jobs.length === 0 ? (
              <EmptyState icon="ri-list-check-2" title="暂无队列" text="提交生成后会显示状态。" />
            ) : (
              <div className="space-y-2">
                {jobs.slice(0, 6).map((job) => (
                  <div key={job.job_row_id} className="rounded-lg border border-secondary-200 bg-background-100 p-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium text-foreground-800 truncate">{job.display_type}</span>
                      <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] ${STATUS_TONE[job.display_status] || 'bg-secondary-100 text-secondary-600 border-secondary-200'}`}>
                        {job.status_label}
                      </span>
                    </div>
                    {job.user_input_preview && <p className="mt-1 text-[10px] text-secondary-500 line-clamp-2">{job.user_input_preview}</p>}
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="rounded-xl border border-secondary-200 bg-background-50 p-3">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-foreground-950">资产库</h2>
              {selected && <Link to={`/materials/${selected.id}`} className="text-xs text-primary-700 font-medium">全部</Link>}
            </div>
            {videos.length === 0 && photos.length === 0 ? (
              <EmptyState icon="ri-folder-video-line" title="暂无资产" text="视频完成后会显示在这里。" />
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {videos.slice(0, 4).map((asset) => (
                  <div key={`v-${asset.id}`} className="aspect-square rounded-lg overflow-hidden bg-secondary-100 border border-secondary-200">
                    {asset.url ? <video src={asset.url} className="w-full h-full object-cover" controls /> : <i className="ri-loader-4-line h-full flex items-center justify-center text-secondary-400" />}
                  </div>
                ))}
                {photos.slice(0, Math.max(0, 4 - videos.length)).map((asset) => (
                  <div key={`p-${asset.id}`} className="aspect-square rounded-lg overflow-hidden bg-secondary-100 border border-secondary-200">
                    {asset.url ? <img src={asset.url} alt="角色形象" className="w-full h-full object-cover" /> : <i className="ri-image-line h-full flex items-center justify-center text-secondary-400" />}
                  </div>
                ))}
              </div>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
}
